<template>
  <div
    class="search-result-panel"
    :class="{
      'page-hina': isHina,
      'page-jps': isJps,
    }"
  >
    <div
      class="left-side"
      v-if="leftFacets.length > 0"
      role="complementary"
      :aria-label="ls(dapLangGlobal.facetMenu)"
    >
      <h2 class="u-screen-reader-only">{{ ls(dapLangGlobal.facetMenu) }}</h2>
      <div class="facets">
        <SearchFacet
          v-for="facet in leftFacets"
          :facet="facet"
          :key="facet.key"
          :facet-length="getLeftFacetShowCount(facet.key)"
          :ss="ss"
        />
      </div>
    </div>
    <div
      class="panels"
      :class="{ 'show-facet': leftFacets.length > 0 }"
    >
      <div
        class="media panel-entry"
        v-for="item in ss.state.result.list"
        :key="item.id"
      >
        <figure class="media-left">
          <p class="image">
            <BaseLink
              :nolink="isWebParts"
              @click.native="emits('show', item)"
              :cache="{ type: 'item', data: item }"
              :to="itemTo(item)"
            >
              <BaseThumbnail
                v-if="!isEmpty(item.common.thumbnailUrl)"
                :src="item.common.thumbnailUrl[0]"
                loading="lazy"
                :error-alt="getNoImage(item)"
                :appearance="{
                  borderRadius: 'medium',
                  hoverScale: true,
                }"
              />
              <BaseThumbnail
                v-else
                :src="getNoImage(item)"
                :appearance="{
                  borderRadius: 'medium',
                  hoverScale: true,
                }"
              />
            </BaseLink>
          </p>
        </figure>
        <div class="media-content">
          <div class="content">
            <div
              class="item-title-container"
            >
              <h3 class="h4 item-title-container-heading">
                <BaseLink
                  :nolink="isWebParts"
                  @click.native="emits('show', item)"
                  :cache="{ type: 'item', data: item }"
                  :to="itemTo(item)"
                  >{{
                    item.common.title
                      ? item.common.title
                      : ls(dapLangGlobal.noTitle)
                  }}</BaseLink
                >
              </h3>
              <span v-if="isHina && (list(item.common.contributor) || list(item.common.location))" class="hina-description">
                {{ abb(list(item.common.contributor), 100) }}
                {{ abb(list(item.common.location), 100) }}
              </span>
            </div>
            <div class="item-meta">
              <!-- JPS 用コンテンツ -->
              <template v-if="isJps">
                <UiRightsTag
                  simple
                  :rights="item.common.contentsRightsType"
                  :rights-description="
                    dataStore.database(item.common.database)
                      ? dataStore.database(item.common.database).contentsRights
                      : null
                  "
                  type="item"
                  :data="item"
                />
                <p class="jps-item-details">
                  {{
                    joinListsToStr(
                      item.common.contributor,
                      item.common.temporal,
                      item.common.location,
                    )
                  }}
                </p>
                <p>
                  {{ ls(dapLangGlobal.provider) }}:
                  <UiOrgProviderSpan
                    tag="BaseLink"
                    :id="item.common.provider"
                    :csid="ss.state.searchdef.id"
                    :nolink="isWebParts"
                  />
                </p>
                <p
                  v-if="
                  item.common.ownerOrg &&
                  item.common.provider !== item.common.ownerOrg
                "
                >
                  {{ ls(dapLangGlobal.agregator) }}:
                  <BaseLink :to="'/organization/' + item.common.ownerOrg" :nolink="isWebParts" class="item-owner">
                    <UiOrgSpan :id="item.common.ownerOrg" />
                  </BaseLink>
                </p>
              </template>

              <!-- ひなぎく用コンテンツ -->
              <template v-else-if="isHina">
                <div class="hina-contents-type-meta">
                  <UiItemContentsTypeIcon :contents-type="item.common.contentsType" />
                  <span>{{ abb(list(item.common.temporal), 100) }}</span>
                </div>
              </template>

              <div class="level item-database">
                <p class="item-database-provider">
                  <UiDbSpan
                    :nolink="isWebParts"
                    :id="item.common.database"
                    class="layout-row item-database-text"
                    :class="{ 'is-link': !isWebParts }"
                  >
                    <template #default="{ db }">
                      <BaseIcon name="mdi/database" size="size-6" class="icon" />
                      <span class="text">{{ ls(db?.name) }}</span>
                    </template>
                  </UiDbSpan>
                </p>
                <div class="item-database-link-list">
                  <div class="item-database-external-link" v-if="isJps || item.common.linkUrl">
                    <UiButtonLinkRound
                      :url="item.common.linkUrl"
                      styled="primary"
                      size="large"
                    >
                      {{ ls(dapLangGlobal.linkToSource) }}
                    </UiButtonLinkRound>
                  </div>
                  <UiInteractPanel type="item" :data="item">
                    <BaseLink
                      class="image-search-icon"
                      :searchscroll="true"
                      v-if="item.common.thumbnailUrl"
                      aria-label="Image Search"
                      :to="imageSearchTo(item)"
                    >
                      <BaseIcon name="mdi/image-search-outline" :alt="ls(dapLangGlobal.imageSearch)" />
                    </BaseLink>
                  </UiInteractPanel>
                </div>
              </div>
              <p class="jps-item-desc">
                {{ abb(lid(item), 100) }}
              </p>
              <ClientOnly>
                <UiItemSearchHighlight :item="item" :keywords="words" />
              </ClientOnly>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { dapLangGlobal } from '#imports'
import { useSearchResult } from '~/components/ui/item/search/useSearchResult'

const { isWebParts } = useWebParts()
const { ls, lid } = useI18n()
const { isJps, isHina } = useServiceType()
const dataStore = useDataStore()

interface Props {
  test?: boolean
  words: string[]
  ss: SearchStore<Item>
}
const props = withDefaults(defineProps<Props>(), {
  test: false
})

const emits = defineEmits(['show'])

const {
  leftFacets,
  getLeftFacetShowCount,
  getNoImage,
  itemTo,
  list,
  imageSearchTo
} = useSearchResult(props.ss, props.test)
</script>

<style lang="scss" scoped>
@use '@/assets/styles/scss/vue' as *;

.search-result-panel {
  position: relative;
  display: flex;
  flex-direction: row-reverse;
  padding: 0 0 1rem;

  @include is-tablet {
    flex-direction: row;
  }

  .left-side {
    display: none;
    min-width: 17rem;
    @include is-tablet {
      display: block;
    }
  }
  .panels {
    flex-grow: 1;
    &.show-facet {
      @include is-desktop {
        padding-left: calc(0.25rem * 6);
      }
    }
  }
  .panel-entry {
    margin-bottom: 0;
    display: flex;
    gap: 0 1rem;
    align-items: stretch;
    border-radius: 0.5rem;
    border-style: none;
    padding: calc(0.25rem * 4);
    @include transition-transform;

    &:hover {
      background-color: $jps-gray-100;

      :deep(.hover-target) {
        @include is-tablet {
          transform: scale(1.05);
        }
      }
    }

    @include is-mobile {
      padding: 0;
    }
  }

  .media-left {
    flex: 0 0 10rem;
    margin-right: 0;
    @include is-mobile {
      flex: 0 0 4rem;
      margin-right: 0.25rem;
      padding-top: 0.75rem;
    }
    p {
      text-align: center;
    }

    :deep(img) {
      width: auto !important;
      max-width: 100% !important;
      max-height: 15rem !important;
    }
  }

  .media-content {
    overflow: visible;
    padding: calc(0.25rem * 2);
    overflow-wrap: anywhere;

    .content {
      font-size: 0.875rem; line-height: calc(1.25 / 0.875);
    }
  }

  .item-title-container {
    a {
      word-break: break-all;
    }
  }
  .item-title-container-heading {
    margin: 0;
    font-size: $size-6;
  }

  .jps-item-details {
    margin-bottom: calc(0.25rem * 2);
    display: block;
    font-size: 0.875rem;
    line-height: calc(1.25 / 0.875);
  }
  .item-database {
    margin-bottom: 0;
    @include is-tablet {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
    }
    .level-left {
      width: 100%;
      display: flex;
      align-items: center;
    }
    .level {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
    }
    .jps-tag {
      font-size: 0.875rem; line-height: calc(1.25 / 0.875);
      color: white;
    }
  }

  .item-database-provider {
    display: flex;
    align-items: center;
  }

  .item-database-link-list {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    justify-content: space-between;

    @include is-tablet {
      justify-content: flex-end;
    }
  }

  .item-database-external-link {
    min-height: 3rem;
    display: flex;
    align-items: center;
  }

  .item-meta {
    width: 100%;
    overflow-wrap: anywhere;

    a {
      font-size: $size-8;
      @include is-tablet {
        font-size: $size-7;
      }
    }
  }

  .jps-item-desc {
    font-size: $size-8;
    line-height: calc(1.25 / 0.875);
    color: $jps-gray-600;
  }
}

.item-database-text {
    gap: 0.25rem;
    display: inline-flex;
    align-items: center;
    line-height: 1.2;

    &.is-link {
      color: $jps-key-blue;
    }

    .icon {
      flex-shrink: 0;
    }

    span {
      vertical-align: middle;
    }
}

.page-hina {
  .item-title-container {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.25rem;

    .hina-description {
      font-size: $size-8;
      color: $hina-text-sub;
    }
  }

  .hina-contents-type-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem 1rem;
    color: $hina-text-sub;
  }

  .item-meta {
    margin-top: 0.5rem;
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  .item-meta {
    p {
      margin: 0;
    }
  }
}

.page-jps {
  .item-title-container {
    margin-bottom: calc(0.25rem * 2);
    display: flex;
    align-items: center;
    justify-content: flex-start;
  }

  .item-meta {
    p {
      margin: 0.375rem 0;
    }
  }
}
</style>
